import {
  faCircleLeft,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Roles } from "../../types/user.types";
import { IpWhitelistEntry } from "../../types/ip-whitelist.types";
import { handleError } from "../../common/utils";
import { useIpWhitelistService } from "../../services/ip-whitelist.service";
import ConfirmModal from "../../components/confirm-modal";
import { useAuthGuard } from "../../hooks/useAuthGuard";
import { InlineSpinner } from "../../components/inline-spinner";
import * as ipaddr from "ipaddr.js";

const MIN_CIDR_PREFIX = 9;

function isValidIpOrCidr(input: string): boolean {
  const trimmed = input.trim();

  if (ipaddr.isValid(trimmed)) return true;

  if (ipaddr.isValidCIDR(trimmed)) {
    const [, prefixLength] = ipaddr.parseCIDR(trimmed);
    return prefixLength >= MIN_CIDR_PREFIX;
  }

  return false;
}

// Client-side convenience check for the lockout warning UX.
// The backend (IpWhitelistService.IsIpCoveredByEntry) is the authoritative check.
function isIpInEntry(clientIp: string, entryIpOrCidr: string): boolean {
  if (!ipaddr.isValid(clientIp)) return false;

  const parsed = ipaddr.parse(clientIp);

  if (ipaddr.isValid(entryIpOrCidr)) {
    return parsed.toString() === ipaddr.parse(entryIpOrCidr).toString();
  }

  if (ipaddr.isValidCIDR(entryIpOrCidr)) {
    return parsed.match(ipaddr.parseCIDR(entryIpOrCidr));
  }

  return false;
}

export default function IpWhitelist() {
  useAuthGuard(Roles.Admin);

  const [entries, setEntries] = useState<IpWhitelistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: number; ipAddress: string } | null>(null);
  const [filter, setFilter] = useState<"all" | "enabled" | "disabled">("all");

  // Add form state
  const [newIpAddress, setNewIpAddress] = useState("");
  const [newFriendlyName, setNewFriendlyName] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");

  // Success/warning message for overlap replacements
  const [addSuccess, setAddSuccess] = useState("");

  // Client IP state for lockout prevention
  const [clientIp, setClientIp] = useState<string | null>(null);
  const [showLockoutWarning, setShowLockoutWarning] = useState(false);

  const { getAll, add, update, remove, getClientIp } = useIpWhitelistService();

  const loadEntries = useCallback(async () => {
    setErrorMessage("");
    setLoading(true);

    try {
      const result = await getAll();
      setEntries(result);
    } catch (error) {
      handleError(error, setErrorMessage, () => {});
    } finally {
      setLoading(false);
    }
  }, [getAll]);

  const loadClientIp = useCallback(async () => {
    try {
      const result = await getClientIp();
      setClientIp(result.ipAddress);
    } catch (error) {
      // Silently fail - client IP detection is optional
      console.error("Failed to detect client IP:", error);
    }
  }, [getClientIp]);

  useEffect(() => {
    loadEntries();
    loadClientIp();
  }, [loadEntries, loadClientIp]);

  const handleAdd = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setAddError("");
    setAddSuccess("");

    if (!newIpAddress.trim()) {
      setAddError("IP address is required");
      return;
    }

    if (!newFriendlyName.trim()) {
      setAddError("Rule name is required");
      return;
    }

    if (!isValidIpOrCidr(newIpAddress)) {
      setAddError(
        "Enter a valid IP address (e.g., 192.168.1.1) or CIDR range (e.g., 10.0.0.0/24). Minimum prefix length is /9."
      );
      return;
    }

    // Client-side duplicate validation
    const ipExists = entries.some(
      (entry) => entry.ipAddress === newIpAddress.trim()
    );
    if (ipExists) {
      setAddError("An entry with this IP address already exists");
      return;
    }

    const nameExists = entries.some(
      (entry) => entry.friendlyName === newFriendlyName.trim()
    );
    if (nameExists) {
      setAddError("An entry with this rule name already exists");
      return;
    }

    setAddLoading(true);

    try {
      const response = await add({
        ipAddress: newIpAddress.trim(),
        friendlyName: newFriendlyName.trim(),
      });
      setNewIpAddress("");
      setNewFriendlyName("");
      if (response.message && response.message !== "Entry added to whitelist") {
        setAddSuccess(response.message);
      }
      await loadEntries();
    } catch (error) {
      handleError(error, setAddError, () => {});
    } finally {
      setAddLoading(false);
    }
  };

  const handleToggleEnabled = async (entry: IpWhitelistEntry) => {
    // Prevent user from disabling their own IP
    if (entry.isEnabled && clientIp && isIpInEntry(clientIp, entry.ipAddress)) {
      setShowLockoutWarning(true);
      return;
    }

    setActionLoading(entry.id);

    try {
      await update(entry.id, {
        friendlyName: entry.friendlyName,
        isEnabled: !entry.isEnabled,
      });
      await loadEntries();
    } catch (error) {
      handleError(error, setErrorMessage, () => {});
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;

    setActionLoading(pendingDelete.id);
    setShowConfirmModal(false);

    try {
      await remove(pendingDelete.id);
      await loadEntries();
    } catch (error) {
      handleError(error, setErrorMessage, () => {});
    } finally {
      setActionLoading(null);
      setPendingDelete(null);
    }
  };

  const confirmDelete = (id: number, ipAddress: string) => {
    setPendingDelete({ id, ipAddress });
    setShowConfirmModal(true);
  };

  const filteredEntries = entries.filter((entry) => {
    if (filter === "enabled") return entry.isEnabled;
    if (filter === "disabled") return !entry.isEnabled;
    return true;
  });

  const enabledCount = entries.filter((e) => e.isEnabled).length;
  const disabledCount = entries.filter((e) => !e.isEnabled).length;

  return (
    <div className="container">
      <div className="d-flex flex-row mb-4">
        <div className="me-auto" style={{ marginLeft: "-12px" }}>
          <Link to="/admin" className="btn btn-sw btn-sm btn-outline">
            <FontAwesomeIcon icon={faCircleLeft} className="me-2" />
            Back to Admin
          </Link>
        </div>
      </div>
      <div className="row tablet-view">
        <div className="col-4 tablet-left d-flex flex-column">
          <div className="tablet-buffer d-flex flex-fill flex-column mb-3">
            <span>Administrator</span>
            <h2>IP Whitelist</h2>
          </div>
        </div>
        <div className="col-8 tablet-right">
          <div className="tablet-buffer">
            <h3 className="mb-3">Firewall Rules</h3>

            {addError && (
              <div className="alert alert-danger py-2 mb-3" role="alert">
                {addError}
              </div>
            )}

            {addSuccess && (
              <div className="alert alert-warning py-2 mb-3" role="alert">
                {addSuccess}
              </div>
            )}

            {loading ? (
              <div className="text-center py-4">
                <InlineSpinner /> Loading IP whitelist...
              </div>
            ) : (
              <>
                <div style={{ maxHeight: 500, overflowY: "auto", overflowX: "hidden" }}>
                  <table className="sw">
                    <thead>
                      <tr className="d-flex">
                        <th scope="col" className="col-sm-4 ps-3">Rule Name</th>
                        <th scope="col" className="col-sm-4 ps-3">IP Address</th>
                        <th scope="col" className="col-sm-2 text-center">Enabled</th>
                        <th scope="col" className="col-sm-2 text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Inline add row */}
                      <tr className="d-flex border-bottom bg-light">
                        <td className="col-sm-4">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Rule name"
                            value={newFriendlyName}
                            onChange={(e) => setNewFriendlyName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newIpAddress.trim() && newFriendlyName.trim()) {
                                handleAdd();
                              }
                            }}
                            disabled={addLoading}
                          />
                        </td>
                        <td className="col-sm-4">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="IP Address or CIDR (e.g., 10.0.0.0/24)"
                            value={newIpAddress}
                            onChange={(e) => setNewIpAddress(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newIpAddress.trim() && newFriendlyName.trim()) {
                                handleAdd();
                              }
                            }}
                            disabled={addLoading}
                          />
                        </td>
                        <td className="col-sm-2 text-center">
                          {/* Enabled column - empty for add row */}
                        </td>
                        <td className="col-sm-2 text-center">
                          <button
                            className="btn btn-sm btn-sw"
                            onClick={() => handleAdd()}
                            disabled={addLoading || !newIpAddress.trim() || !newFriendlyName.trim()}
                          >
                            {addLoading ? <InlineSpinner /> : "Add"}
                          </button>
                        </td>
                      </tr>
                      {filteredEntries.length > 0 ? (
                        filteredEntries.map((entry) => (
                          <tr key={entry.id} className="d-flex border-bottom">
                            <td className="col-sm-4 ps-3">{entry.friendlyName}</td>
                            <td className="col-sm-4 ps-3">{entry.ipAddress}</td>
                            <td className="col-sm-2 text-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={entry.isEnabled}
                                onChange={() => handleToggleEnabled(entry)}
                                disabled={actionLoading === entry.id}
                              />
                            </td>
                            <td className="col-sm-2 text-center">
                              {actionLoading === entry.id ? (
                                <InlineSpinner />
                              ) : (
                                <button
                                  className="btn btn-sm btn-sw-m"
                                  onClick={() => confirmDelete(entry.id, entry.ipAddress)}
                                  title="Delete"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : entries.length === 0 ? (
                        <tr className="d-flex border-bottom">
                          <td colSpan={4} className="col-sm-12 text-muted text-center py-3">
                            No IP addresses in whitelist. All traffic is currently allowed.
                          </td>
                        </tr>
                      ) : (
                        <tr className="d-flex border-bottom">
                          <td colSpan={4} className="col-sm-12 text-muted text-center py-3">
                            No entries match the current filter
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-start mt-3">
                  <div className="btn-group">
                    <button
                      className={`btn btn-sm ${filter === "all" ? "btn-sw" : "btn-outline-secondary"}`}
                      onClick={() => setFilter("all")}
                    >
                      All ({entries.length})
                    </button>
                    <button
                      className={`btn btn-sm ${filter === "enabled" ? "btn-sw" : "btn-outline-secondary"}`}
                      onClick={() => setFilter("enabled")}
                    >
                      Enabled ({enabledCount})
                    </button>
                    <button
                      className={`btn btn-sm ${filter === "disabled" ? "btn-sw" : "btn-outline-secondary"}`}
                      onClick={() => setFilter("disabled")}
                    >
                      Disabled ({disabledCount})
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {errorMessage && (
            <div className="row mt-3">
              <div className="col">
                <div className="alert alert-danger" role="alert">
                  {errorMessage}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        message={
          pendingDelete
            ? `Are you sure you want to remove ${pendingDelete.ipAddress} from the whitelist?`
            : ""
        }
        action={handleDelete}
        isOpen={showConfirmModal}
        close={() => {
          setShowConfirmModal(false);
          setPendingDelete(null);
        }}
      />

      <ConfirmModal
        title="Warning"
        message="You cannot disable your own IP address. This would lock you out of the system."
        action={() => {}}
        isOpen={showLockoutWarning}
        close={() => setShowLockoutWarning(false)}
        confirmOnly
        confirmText="OK"
      />
    </div>
  );
}
